import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CATALOGUE_ENABLED } from '../config/features';

// While the Catalogue is hidden, send catalogue/product CTAs to Contact instead.
const fixCta = (link: string, label: string) =>
  (!CATALOGUE_ENABLED && (link.startsWith('/catalogue') || link.startsWith('/product')))
    ? { to: '/contact', label: 'Get in touch' }
    : { to: link, label };


const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  // removed unused state
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = [
    {
      id: 1,
      title: 'Keep Smiles Wide Open',
      subtitle: 'with OptraGate',
      description: 'Revolutionary mouth retractor technology for optimal access and patient comfort during dental procedures.',
      image: '/images/clinic.jpg',
      ctaPrimary: 'Learn More About OptraGate',
      ctaSecondary: 'Explore Catalogue',
      linkPrimary: '/products/optragate',
      linkSecondary: '/catalogue',
    },
    {
      id: 2,
      title: 'Advanced Dental Equipment',
      subtitle: 'from BMS Dental & Vatech',
      description: 'State-of-the-art dental chairs, units, and imaging systems revolutionizing dental care delivery.',
      image: '/images/machines.jpg',
      ctaPrimary: 'Explore Equipment',
      ctaSecondary: 'Get Quote',
      linkPrimary: '/catalogue?category=machines-equipment',
      linkSecondary: '/contact',
    },
    {
      id: 3,
      title: 'Premium Lab Materials',
      subtitle: 'for Precision Restorations',
      description: 'High-quality laboratory materials and CAD/CAM solutions ensuring precision and durability in every restoration.',
      image: '/images/lab.jpg',
      ctaPrimary: 'View Lab Materials',
      ctaSecondary: 'Request Catalog',
      linkPrimary: '/catalogue?category=laboratory-materials',
      linkSecondary: '/contact',
    },
  ];

  // Preload all images
  useEffect(() => {
    const preloadImages = async () => {
      const imagePromises = slides.map((slide, index) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            console.log(`Image ${index + 1} loaded:`, slide.image);
            resolve(img);
          };
          img.onerror = () => {
            console.error(`Image ${index + 1} failed to load:`, slide.image);
            reject(new Error(`Failed to load ${slide.image}`));
          };
          img.src = slide.image;
        });
      });

      try {
        await Promise.all(imagePromises);
        setImagesLoaded(true);
        console.log('All images preloaded successfully');
      } catch (error) {
        console.error('Error preloading images:', error);
        setImagesLoaded(true); // Still show content even if images fail
      }
    };

    preloadImages();
  }, []);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isPlaying, slides.length]);

  // Debug: Log current slide image
  useEffect(() => {
    console.log('Current slide image:', slides[currentSlide].image);
  }, [currentSlide]);

  // Preload next image for smoother transitions
  useEffect(() => {
    if (imagesLoaded) {
      const nextSlideIndex = (currentSlide + 1) % slides.length;
      const nextImage = new Image();
      nextImage.src = slides[nextSlideIndex].image;
    }
  }, [currentSlide, imagesLoaded, slides]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 2000);
  };




  return (
    <div className="relative w-full h-[50vh] sm:h-[60vh] lg:h-[80vh] overflow-hidden pt-16 sm:pt-20">
      {/* Loading Indicator */}
      {!imagesLoaded && (
        <div className="absolute inset-0 bg-black flex items-center justify-center z-50">
          <div className="text-center">
            <div className="relative">
              <div className="w-20 h-20 border-4 border-[#8EC641]/20 rounded-full"></div>
              <div className="absolute top-0 left-0 w-20 h-20 border-4 border-[#8EC641] rounded-full border-t-transparent animate-spin"></div>
            </div>
            <p className="text-white text-lg mt-6 font-medium">Loading your dental solutions...</p>
          </div>
        </div>
      )}

      {/* Slides */}
      <AnimatePresence mode="sync">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="absolute inset-0"
          onAnimationStart={() => setIsTransitioning(true)}
          onAnimationComplete={() => setIsTransitioning(false)}
        >
          {/* Background Image */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${slides[currentSlide].image})`,
            }}
          />
          
          {/* Text Readability Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Content - Left Aligned */}
      <div className="relative z-10 flex items-center h-full">
        <div className="container-custom px-4 sm:px-6 lg:px-8 flex justify-start">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: -80, y: 20 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 80, y: -20 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-xl text-left ml-[-8] pt-8 sm:pt-0"
          >

            {/* Main Title */}
            <motion.h1 
              className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight text-white drop-shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.8 }}
            >
              {slides[currentSlide].title}
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              className="text-lg sm:text-2xl md:text-3xl text-[#8EC641] mb-6 font-semibold drop-shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
            >
              {slides[currentSlide].subtitle}
            </motion.p>

            {/* Description */}
            <motion.p 
              className="text-base sm:text-lg md:text-xl text-gray-200 mb-8 max-w-xl leading-relaxed drop-shadow-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
            >
              {slides[currentSlide].description}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              {(() => {
                const a = fixCta(slides[currentSlide].linkPrimary, slides[currentSlide].ctaPrimary);
                const b = fixCta(slides[currentSlide].linkSecondary, slides[currentSlide].ctaSecondary);
                const dupe = a.to === b.to && a.label === b.label;
                return (
                  <>
                    <Link
                      to={a.to}
                      className="group inline-flex items-center justify-center bg-[#0D56A6] hover:bg-[#0A4A8F] text-white font-bold text-sm sm:text-lg px-6 py-3 sm:px-8 sm:py-4 rounded-xl shadow-2xl hover:shadow-[#0D56A6]/30 transition-all duration-300 transform hover:scale-105 border border-[#0D56A6]/20"
                    >
                      {a.label}
                    </Link>
                    {!dupe && (
                      <Link
                        to={b.to}
                        className="group inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-bold text-sm sm:text-lg px-6 py-3 sm:px-8 sm:py-4 rounded-xl border-2 border-white/30 shadow-2xl hover:shadow-white/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
                      >
                        {b.label}
                      </Link>
                    )}
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        </div>
      </div>




      {/* Elegant Dots Navigation */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-4">
        {slides.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => goToSlide(index)}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
            disabled={isTransitioning}
            className={`transition-all duration-500 ease-out ${
              index === currentSlide
                ? 'w-8 h-2 sm:w-12 sm:h-3 bg-[#8EC641] rounded-full shadow-lg shadow-[#8EC641]/50'
                : 'w-2 h-2 sm:w-3 sm:h-3 bg-white/60 hover:bg-white rounded-full'
            }`}
          />
        ))}
      </div>


      {/* Animated Progress Bar */}
      <motion.div 
        className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#8EC641] to-[#0D56A6]"
        initial={{ width: 0 }}
        animate={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        transition={{ duration: 1, ease: "easeInOut" }}
      />


    </div>
  );
};

export default HeroCarousel;
